import { getHassNodeSchema, HASS_ACTION_VARIANTS } from '../Hasp/properties/v2/hass/index.js';
import { isMediaPlayerService } from '../automation/mediaPlayerAction.js';

/**
 * The action editor's task type of an action, from its JSON (as
 * ActionEditor.vue detectTaskType does for a rule's single action).
 */
export function hassActionTaskType(data) {
    if (data?.type === 'delay') return 'delay';
    if (data?.editorMetadata?.operation === 'tts') return 'media_player';
    if (data?.provider === 'media_player' && (data.service === 'play_media' || isMediaPlayerService(data.service))) return 'play_media';
    if (data?.provider === 'scene' && data.service === 'turn_on') return 'activate_scene';
    if (data?.type === 'call_service' && ['toggle', 'turn_on', 'turn_off'].includes(data.service)) return data.service;
    return 'advanced';
}

/** The palette variant an action node currently is. */
export function hassActionVariant(node) {
    const taskType = hassActionTaskType(node?.data);
    return HASS_ACTION_VARIANTS.find(variant => (taskType === 'delay'
        ? variant.nodeType === 'delay'
        : variant.preset.taskType === taskType)) ?? null;
}

const operand = value => {
    if (value?.kind === 'reference') return value.name;
    if (value?.kind === 'template') return value.template;
    if (value?.kind === 'entity') return value.attribute ? `entity.${value.attribute}` : 'entity state';
    return value?.value ?? '';
};

/** One-line form of a condition node's conditions. */
export function describeHassConditions(data) {
    const parts = [];
    if (data?.filter?.val !== undefined) parts.push(`button ${data.filter.val}`);
    for (const condition of data?.conditions ?? []) {
        parts.push(`${operand(condition.left)} ${condition.operator} ${operand(condition.right)}`);
    }
    return parts.join(' and ');
}

const serviceName = data => (data?.provider && !String(data.service ?? '').includes('.')
    ? `${data.provider}.${data.service ?? ''}`
    : data?.service ?? '');

function bindingSummary(data) {
    if (!data?.property) return 'Select a property';
    switch (data.strategy) {
    case 'constant': return `${data.property} = ${data.value ?? ''}`;
    case 'template': return `${data.property} = ${data.template || 'template'}`;
    case 'mapping': return `${data.property} · ${(data.rules ?? []).length} rules`;
    default: return data.attribute ? `${data.property} ← ${data.attribute}` : `${data.property} ← state`;
    }
}

function actionSummary(data) {
    if (data?.type === 'delay') {
        const seconds = data.parameters?.find(parameter => parameter.key === 'seconds')?.value?.value;
        return seconds ? `${seconds} s` : 'Set a delay';
    }
    if (data?.editorMetadata?.operation === 'tts') {
        const text = (data.editorMetadata.message?.nodes ?? []).map(node => node.text ?? '').join('').trim();
        return text ? `“${text}”` : 'Enter a message';
    }
    return serviceName(data) || 'Select an action';
}

/**
 * Short, human-readable title and summary of a Home Assistant flow node,
 * for the canvas. Never authoritative: validation lives in the model.
 */
export function describeHassNode(node) {
    const schema = getHassNodeSchema(node?.type);
    const data = node?.data ?? {};
    const base = { icon: schema?.icon ?? '?', color: schema?.color ?? '#64748b', title: schema?.label ?? node?.type ?? '?' };
    switch (schema?.kind) {
    case 'entity':
        return { ...base, summary: data.entityId || 'Select an entity' };
    case 'binding':
        return { ...base, summary: bindingSummary(data) };
    case 'switch':
        return { ...base, summary: data.attribute || 'state', outputLabels: true };
    case 'trigger': {
        if (data.event) return { ...base, summary: `on ${String(data.event).replace(/_/g, ' ')}` };
        // A state trigger has no event; its summary is the change it waits for.
        const change = [data.attribute, data.from && `from ${data.from}`, data.to && `to ${data.to}`].filter(Boolean).join(' ');
        return { ...base, summary: change ? `state ${change}` : 'Select an event or state' };
    }
    case 'condition':
        return { ...base, summary: describeHassConditions(data) || 'Add a condition' };
    case 'action': {
        const variant = hassActionVariant(node);
        return { ...base, icon: variant?.icon ?? base.icon, title: variant?.label ?? base.title, summary: actionSummary(data) };
    }
    default:
        return { ...base, summary: '' };
    }
}
